import { Box, Typography } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import { useGetAccountAccess } from "@dealer-center/ddc-pages-configuration-fetch";
import type { OperatorAuthority } from "@dealer-center/ddc-pages-configuration-interface";
import type { UserInfoSubject } from "../UserInfoSubject";
import { SPACE_BLOCK } from "../layoutTokens";
import { ReadOnlyNotice } from "../ReadOnlyNotice";
import { TabState } from "../TabState";
import { useUserInfoArea } from "../userInfoAreaContext";
import { AccountNode } from "./AccountNode";

export interface AccountAccessTabProps {
  subject: UserInfoSubject;
  /**
   * The **operator's** authority, forwarded to each node. **Absent means read-only** — see
   * `FeatureAccessTabProps.authority`. `reachableAccountIds` is what FR-042 tests against; it comes
   * from the operator's **account-access** self-read, which is the half of the grantable set
   * FR-058's prose omits (FR-058b).
   */
  authority?: OperatorAuthority;
  /**
   * Whether the record on screen belongs to the **operator** — the person signed in.
   *
   * Selects the intro line's copy register, and nothing else. Do **not** conflate it with
   * `authority`: this used to be derived from `authority` being absent, which held only by
   * coincidence. Authority is withheld for **two** unrelated reasons — the subject is the operator
   * (FR-009/FR-055), *or* the operator's account-access self-read failed — and after the second
   * stopped denying the whole page (review 2026-09-15) an `authority`-based test would have claimed
   * another user's account list as the reader's own on any such failure.
   *
   * Defaults to `false`, the impersonal register. See `LoginHistoryTabProps` for why that direction.
   */
  isSubjectTheOperator?: boolean;
}

/**
 * The subject's account hierarchy, nested to arbitrary depth (FR-028–FR-031); editable per FR-042.
 *
 * **Every returned node is listed, granted or not** (FR-031). That is T002's recorded outcome, not an
 * assumption: sampled across eight subjects on 2026-08-21 the node set was *identical* every time, so
 * the tree is the full hierarchy rather than a pre-filtered list of reachable accounts; and
 * `writeAccess: false` correlated exactly with holding zero `USER_ACL_*` grants in that account across
 * nine accounts, so it means access-vs-none rather than write-vs-read. Both readings mattered — under
 * the write-vs-read reading, rendering it as granted/not-granted would tell a user with read-only
 * access that they have none.
 *
 * **This tab does not decide whether it is shown.** FR-006's omission is the tab strip's job, driven
 * by `hasAccountHierarchy` in `MyInfo`, because the tab has to appear *before* this query resolves —
 * it is the slowest endpoint (~5–6s) and blocking the strip on it would leave the page empty for
 * seconds. So a tab that renders at all renders content, loading, or failure — never "not applicable".
 */
export const AccountAccessTab = ({
  subject,
  authority,
  isSubjectTheOperator = false
}: AccountAccessTabProps) => {
  const { data, isPending, isError } = useGetAccountAccess({
    userId: subject.userId,
    accountId: subject.accountId
  });

  const roots = data?.accountPermissions ?? [];
  // Telemetry attribution only — see `userInfoAreaContext`.
  const area = useUserInfoArea();

  return (
    <TabState
      isEmpty={roots.length === 0}
      isError={isError}
      isLoading={isPending}
      loadingActionName={`Loading ${area.label} account access`}
      testId={"account-access"}
    >
      <Box
        sx={{ display: "flex", flexDirection: "column", rowGap: SPACE_BLOCK }}
      >
        {/*
         * Read-only only. Always true for My Info (it passes no `authority`), so its shipped
         * behavior is unchanged; for User Manager this is exactly FR-009/FR-055's self-selection.
         */}
        {!authority && <ReadOnlyNotice />}

        {/*
         * The approved prototype's intro copy. It answers the question the bare tree provokes — a
         * list of checked and unchecked accounts, some of them not grantable, invites "what am I
         * looking at, and why can't I change it?" — and it names the relationship to Feature Access,
         * which is otherwise only discoverable by opening that tab and inferring it.
         */}
        <Typography
          color={"sem.color.on-surface.subdued"}
          data-testid={"account-access-intro"}
          tag={"p"}
          variant={"body-sm"}
        >
          {/*
           * Two variants, because the shipped copy is first person — "the accounts **your login**
           * can reach" — which describes the operator and is wrong whenever the subject is someone
           * else. My Info keeps its wording; User Manager gets the subject-neutral one.
           *
           * Keyed on `isSubjectTheOperator`, **not** on `authority` being absent, which is what it
           * read until 2026-09-15 — see the prop's comment for the two unrelated reasons authority
           * goes missing.
           */}
          {
            Locale.getText(
              isSubjectTheOperator ? "ACCT_INTRO" : "UM_ACCT_INTRO"
            ) as string
          }
        </Typography>

        {/*
         * A single list holds the roots; `AccountNode` supplies each `<li>` and recurses. Real nested
         * lists rather than flattened rows with indentation, so the hierarchy is conveyed
         * programmatically (FR-050).
         */}
        <Box
          component={"ul"}
          data-testid={"account-access-tree"}
          sx={{ margin: 0, paddingLeft: 0 }}
        >
          {roots.map(root => (
            <AccountNode
              authority={authority}
              key={root.accountId}
              node={root}
            />
          ))}
        </Box>
      </Box>
    </TabState>
  );
};
